import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { subscribeToMaintenanceStatus } from './lib/maintenanceService';
import { conditionalLog } from './lib/consoleLogger';

// Load cache testing utilities (including forceUpgrade for production troubleshooting)
import('./lib/cacheTestUtils').catch(err => 
  conditionalLog('Cache test utils not loaded:', err)
);

// Initialize IndexedDB at app startup
import('./lib/indexedDBUtils').then(({ initializeIndexedDB }) => {
  initializeIndexedDB().catch(err => 
    conditionalLog('IndexedDB initialization failed:', err)
  );
}).catch(err => conditionalLog('IndexedDB utils not loaded:', err));
import ProtectedRoute from './components/ProtectedRoute';
import RoleGuard from './components/RoleGuard';
import Dashboard from './components/Dashboard';
import InvitationRegister from './components/InvitationRegister';
import UserManagement from './components/UserManagement';
import UserProfile from './components/UserProfile';
import UserActivityDashboard from './components/UserActivityDashboard';
import TechniciansList from './components/TechniciansList';
import TechnicianDetail from './components/TechnicianDetail';
import ClientsList from './components/ClientsList';
import ClientDetail from './components/ClientDetail';
import BlacklistIncidentDetail from './components/BlacklistIncidentDetail';
import OrdersList from './components/OrdersList';
import OrderDetail from './components/OrderDetail';
import OrderReport from './components/OrderReport';
import OrderFormPage from './components/OrderFormPage';
import Equipment from './components/Equipment';
import EquipmentDetail from './components/EquipmentDetail';
import EquipmentForm from './components/EquipmentForm';
import Settings from './components/Settings';
import MaintenancePage from './components/MaintenancePage';

// Maintenance wrapper component
const MaintenanceWrapper = ({ children }) => {
  const [maintenanceStatus, setMaintenanceStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = subscribeToMaintenanceStatus((status) => {
      setMaintenanceStatus(status);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return children(maintenanceStatus);
};

// Enhanced ProtectedRoute wrapper that automatically passes maintenance status
const ProtectedRouteWithMaintenance = ({ maintenanceStatus, ...props }) => (
  <ProtectedRoute {...props} maintenanceStatus={maintenanceStatus} />
);

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <MaintenanceWrapper>
          {(maintenanceStatus) => {
            // Create the ProtectedRoute component with maintenance status pre-filled
            const ProtectedRouteWithStatus = (props) => (
              <ProtectedRouteWithMaintenance maintenanceStatus={maintenanceStatus} {...props} />
            );

            return (
              <Router>
                <Routes>
                  <Route path="/register" element={<InvitationRegister />} />
                  <Route path="/" element={
                    <ProtectedRouteWithStatus>
                      <Dashboard />
                    </ProtectedRouteWithStatus>
                  } />
                  <Route path="/orders" element={
                    <ProtectedRouteWithStatus>
                      <OrdersList />
                    </ProtectedRouteWithStatus>
                  } />
                  <Route path="/orders/new" element={
                    <ProtectedRouteWithStatus>
                      <RoleGuard requiredRoles={['admin', 'shopkeeper']}>
                        <OrderFormPage />
                      </RoleGuard>
                    </ProtectedRouteWithStatus>
                  } />
                  <Route path="/orders/:id" element={
                    <ProtectedRouteWithStatus>
                      <OrderDetail />
                    </ProtectedRouteWithStatus>
                  } />
                  <Route path="/orders/:id/report" element={
                    <ProtectedRouteWithStatus>
                      <OrderReport />
                    </ProtectedRouteWithStatus>
                  } />
                  <Route path="/orders/:id/edit" element={
                    <ProtectedRouteWithStatus>
                      <RoleGuard requiredRoles={['admin', 'shopkeeper']}>
                        <OrderFormPage />
                      </RoleGuard>
                    </ProtectedRouteWithStatus>
                  } />
                  <Route path="/settings" element={
                    <ProtectedRouteWithStatus>
                      <Settings />
                    </ProtectedRouteWithStatus>
                  } />
                  <Route path="/users" element={
                    <ProtectedRouteWithStatus>
                      <RoleGuard requiredRole="admin">
                        <UserManagement />
                      </RoleGuard>
                    </ProtectedRouteWithStatus>
                  } />
                  <Route path="/users/profile" element={
                    <ProtectedRouteWithStatus>
                      <UserProfile />
                    </ProtectedRouteWithStatus>
                  } />
                  <Route path="/users/activity" element={
                    <ProtectedRouteWithStatus>
                      <RoleGuard requiredRole="admin">
                        <UserActivityDashboard />
                      </RoleGuard>
                    </ProtectedRouteWithStatus>
                  } />
                  <Route path="/technicians" element={
                    <ProtectedRouteWithStatus>
                      <TechniciansList />
                    </ProtectedRouteWithStatus>
                  } />
                  <Route path="/technicians/:id" element={
                    <ProtectedRouteWithStatus>
                      <TechnicianDetail />
                    </ProtectedRouteWithStatus>
                  } />
                  <Route path="/clients" element={
                    <ProtectedRouteWithStatus>
                      <ClientsList />
                    </ProtectedRouteWithStatus>
                  } />
                  <Route path="/clients/blacklist" element={
                    <ProtectedRouteWithStatus>
                      <ClientsList />
                    </ProtectedRouteWithStatus>
                  } />
                  <Route path="/clients/blacklist/:incidentId" element={
                    <ProtectedRouteWithStatus>
                      <BlacklistIncidentDetail />
                    </ProtectedRouteWithStatus>
                  } />
                  <Route path="/clients/:id" element={
                    <ProtectedRouteWithStatus>
                      <ClientDetail />
                    </ProtectedRouteWithStatus>
                  } />
                  <Route path="/equipment" element={
                    <ProtectedRouteWithStatus>
                      <Equipment />
                    </ProtectedRouteWithStatus>
                  } />
                  <Route path="/equipment/brands" element={
                    <ProtectedRouteWithStatus>
                      <Equipment />
                    </ProtectedRouteWithStatus>
                  } />
                  <Route path="/equipment/types" element={
                    <ProtectedRouteWithStatus>
                      <Equipment />
                    </ProtectedRouteWithStatus>
                  } />
                  <Route path="/equipment/specs" element={
                    <ProtectedRouteWithStatus>
                      <Equipment />
                    </ProtectedRouteWithStatus>
                  } />
                  <Route path="/equipment/:id" element={
                    <ProtectedRouteWithStatus>
                      <EquipmentDetail />
                    </ProtectedRouteWithStatus>
                  } />
                  <Route path="/equipment/:id/edit" element={
                    <ProtectedRouteWithStatus>
                      <RoleGuard requiredRoles={['admin', 'shopkeeper']}>
                        <EquipmentForm />
                      </RoleGuard>
                    </ProtectedRouteWithStatus>
                  } />
                </Routes>
              </Router>
            );
          }}
        </MaintenanceWrapper>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;